import {useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import {Button, IconButton, Link, Typography} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  CreateScheduleDialog,
  formatEventTemplateType,
  useDeleteVideoEventSchedule,
  useGetVideoSchedules,
  Video,
  VideoEventSchedule,
} from '@entities/video';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {Header} from '@widgets/common';

import {PaginationState} from '@/shared/types';
import {COLOR__WHITE} from '@/theme/colors';

const scheduleColumns: MRT_ColumnDef<VideoEventSchedule>[] = [
  {
    accessorKey: 'templateType',
    header: 'Template type',
    enableSorting: false,
    Cell: ({row}) => formatEventTemplateType(row.original.templateType),
  },
  {
    accessorKey: 'cron',
    header: 'Cron',
    enableSorting: false,
  },
  {
    accessorKey: 'durationMinutes',
    header: 'Duration, min',
    enableSorting: false,
  },
  {
    accessorKey: 'nextRunAt',
    header: 'Next run at',
    Cell: ({row}) => formatInTz(row.original.nextRunAt, DATETIME_FORMAT),
  },
];

export const VideoDetailsPage = () => {
  const {video_id} = useParams<{video_id: string}>();
  const location = useLocation();
  const video = (location.state as {video?: Video} | null)?.video;
  const {enqueueSnackbar} = useSnackbar();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {data, isPending, error} = useGetVideoSchedules(
    {
      videoId: video_id,
      page: urlState.pageIndex + 1,
      pageSize: urlState.pageSize,
    },
    {enabled: !!video_id},
  );

  const {mutate: deleteSchedule} = useDeleteVideoEventSchedule();

  const handleDelete = async (schedule: VideoEventSchedule) => {
    try {
      await openConfirmation({
        title: 'Delete schedule',
        content: 'Are you sure you want to delete this schedule?',
        okButtonText: 'Delete',
      });
    } catch {
      return;
    }

    deleteSchedule(schedule.id, {
      onSuccess: () => {
        enqueueSnackbar('Schedule deleted successfully', {
          variant: 'success',
        });
      },
      onError: err => {
        const errorMessage = isAxiosError(err)
          ? getErrorMessageFromApiError(err)
          : '';
        enqueueSnackbar(`Could not delete schedule. ${errorMessage}`, {
          variant: 'error',
        });
      },
    });
  };

  if (!video_id) {
    return 'Invalid video ID';
  }

  return (
    <>
      <Header title={video?.name ?? `Video ${video_id}`} mb={1} />

      <CardBox bgcolor={COLOR__WHITE} mb={3}>
        <Typography>
          <strong>ID:</strong> {video_id}
        </Typography>
        {video?.channel && (
          <Typography>
            <strong>Channel:</strong> {video.channel}
          </Typography>
        )}
        {video?.videoUrl && (
          <Typography>
            <strong>URL:</strong>{' '}
            <Link
              href={video.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {video.videoUrl}
            </Link>
          </Typography>
        )}
      </CardBox>

      <CardBox bgcolor={COLOR__WHITE}>
        <Header title="Schedules" mb={2} isBackButtonShown={false} />

        <MrTable
          columns={scheduleColumns}
          data={data?.items ?? []}
          error={error}
          renderTopToolbarCustomActions={() => (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              Create schedule
            </Button>
          )}
          renderRowActions={({row}) => (
            <TableRowActionsContainer>
              <IconButton
                size="small"
                onClick={() => void handleDelete(row.original)}
              >
                <DeleteIcon />
              </IconButton>
            </TableRowActionsContainer>
          )}
          enablePagination
          manualPagination
          rowCount={data?.total ?? 0}
          onPaginationChange={setUrl}
          state={{
            isLoading: isPending,
            pagination: urlState,
          }}
          {...entityTableCommonProps}
        />
      </CardBox>

      <CreateScheduleDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        videoId={video_id}
      />
    </>
  );
};
